"use client";

import { useEffect, useRef } from "react";
import { useBlockNumber } from "wagmi";

interface UseBlockRefetchOptions {
  chainId?: number;
  every?: number; // refetch every N blocks (default 1)
  enabled?: boolean;
  // One or more refetch functions returned by wagmi hooks or tanstack queries
  refetch: Array<() => undefined | Promise<any>>;
}

// Lightweight helper: call provided refetch functions every N new blocks.
export function useBlockRefetch({
  chainId,
  every = 1,
  enabled = true,
  refetch,
}: UseBlockRefetchOptions) {
  const lastRefetched = useRef<bigint | undefined>(undefined);
  const { data: blockNumber } = useBlockNumber({ chainId, watch: enabled });

  useEffect(() => {
    if (!enabled || blockNumber === undefined) return;
    if (lastRefetched.current === undefined) {
      lastRefetched.current = blockNumber;
      return;
    }
    const diff = blockNumber - lastRefetched.current;
    if (diff >= BigInt(every)) {
      lastRefetched.current = blockNumber;
      (async () => {
        for (const fn of refetch) {
          try {
            await fn();
          } catch {}
        }
      })();
    }
  }, [blockNumber, enabled, every, refetch]);
}
